import { Toast } from 'antd-mobile';
import { KVStore } from '../utils/KVStore';
import { fileUpload } from '../utils/storageUtils';
import {PickUpPhotoType, PickUpPositionType} from "../pages/pickup/services/PickUpPhotoService";
import {t} from "i18next";

// 定义保存数据的接口
interface StorageItem {
  id: string; // 唯一标识符
  data: any; // 实际存储的数据
  timestamp: number; // 存储时间戳
}

export const PICKED_UP_FAILED_KEY = '__PICKED_UP_FAILED__';

// 定义取件订单数据结构
export interface PickedUpData {
  id: string;
  updatedAt: number;
  photos: Array<{
    type: PickUpPositionType;
    photo: PickUpPhotoType;
  }>;
  exceptionReasonCode?: string;
  type: 'failed' | 'pickedUp' | 'handover' | 'arrivedAtStartingPoint' | 'failedToSec';
  stopNumber?: string; // 揽收点
  channelName?: string; // 客户名称orHub名称
  actualPickupCount?: number;
  /** 揽收备注：司机现场填写，弱网重传时必须带上，否则会静默丢失 */
  driverRemark?: string;
  actualPickupUnit?: number;
  taskId?: string;
  warehouseId?: string;
  cooperationWarehouseId?: string;
  status?: number;
  failedReason?: string;
  // 运单号与「无法扫描」原因互斥，最多一边有值
  trackingNumbers?: string[];
  unscannableReason?: string | null;
  // 扫描时司机 GPS，随扫描记录一并重传
  scanLatitude?: number;
  scanLongitude?: number;
  // BOL 双方签字，base64 PNG
  bolSignatures?: {shipper: string | null; driver: string | null};
  // BOL 单据快照，base64 JPEG。重传时先传它拿 key，传不上去就不提交——没有单据不算揽收完成
  bolImage?: string | null;
  // 揽收站点 2.0 现场联系人，重传时需补一次 submitPickupOnsiteFeedback
  onSiteContact?: {name: string; phone: string; note: string};
  // 现场回传是否仍需补发（灰度 hub 才有值）
  needOnsiteFeedback?: boolean;
}

// 存储类型前缀
const PICKED_UP_STORAGE_KEY = 'picked_up';

/**
 * 将Base64字符串转换为File对象
 * @param base64Data Base64字符串
 * @returns Promise<File> 文件对象
 */
export const PickUpBase64ToFile = async (base64Data: string, filename: string): Promise<File> => {
  // 从Base64中提取数据部分和MIME类型
  const arr = base64Data.split(',');
  const mime = arr[0].match(/:(.*?);/)![1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  // 创建文件对象
  console.log('creating file from base64, filename:', filename);
  return new File([u8arr], filename, { 
    type: mime,
    lastModified: Date.now()
  });
};

export const uploadFailedPickUpPhotos = async (photos: PickUpPhotoType[]): Promise<string[]> => {
  console.log('uploadFailedPhotos:', photos.map(item => item.objectKey));

  const uploadOnePhoto = async (objectUrl: string): Promise<string> => {
    return (await fileUpload(await PickUpBase64ToFile(objectUrl,Date.now() + '.webp'))).objectKey;
  };

  return await Promise.all(photos.map(async (photo) => {
    if (photo.objectKey !== PICKED_UP_FAILED_KEY) {
      return photo.objectKey;
    }

    return await uploadOnePhoto(photo.url);
  }));
};

/**
 * 带超时控制的图片上传方法
 * @param photos 需要上传的图片数组
 * @param timeoutMs 超时时间（毫秒），默认10秒（弱网下更不易误判）
 * @param showToast 是否显示上传进度提示，默认true
 * @returns Promise<string[]> 上传成功后的图片key数组
 */
export const uploadPhotosWithTimeout = async (
    photos: PickUpPhotoType[],
    timeoutMs: number = 10000,
    showToast: boolean = true
): Promise<string[]> => {
  let uploadingToast: any = null;

  if (showToast) {
    uploadingToast = Toast.show({
      content: t('pages.pickUp.uploadPhotoText'),
      position: 'center',
      duration: 0,
    });
  }

  try {
    // 创建超时 Promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        const error = new Error('网络异常：图片上传超时');
        // 添加 networkError 属性，使其被识别为网络异常
        (error as any).networkError = true;
        reject(error);
      }, timeoutMs);
    });

    // 创建上传 Promise
    const uploadPromise = uploadFailedPickUpPhotos(photos);

    // 使用 Promise.race 来实现超时控制
    const photoKeys = await Promise.race([uploadPromise, timeoutPromise]);

    // 上传成功，关闭 Toast
    if (uploadingToast) {
      uploadingToast.close();
    }

    return photoKeys;
  } catch (error) {
    // 上传失败或超时，关闭 Toast
    if (uploadingToast) {
      uploadingToast.close();
    }
    throw error;
  }
};


/**
 * 取件数据服务
 * 用于管理揽收任务的数据存储、检索和同步
 */
class PickedUpDataService {

  private kvStore: KVStore;

  constructor() {
    this.kvStore = new KVStore(PICKED_UP_STORAGE_KEY);
  }

  /**
   * 保存揽收数据
   * @param data 揽收数据
   * @returns 保存成功返回true，失败返回false
   */
  async savePickedUpData(data: PickedUpData): Promise<boolean> {
    try {
      // 确保有更新时间
      if (!data.updatedAt) {
        data.updatedAt = Date.now();
      }

      const storageItem: StorageItem = {
        id: data.id.toString(),
        data: data,
        timestamp: data.updatedAt
      };

      // 保存到本地存储
      return await this.kvStore.setItem(data.id.toString(), storageItem);
    } catch (error) {
      console.error('Failed to save picked up order:', error);
      Toast.show({
        content: '保存取件数据失败',
        position: 'bottom',
      });
      return false;
    }
  }
  
  /**
   * 获取所有取件订单数据
   * @returns 返回所有订单数据数组
   */
  async getAllPickedUpData(): Promise<PickedUpData[]> {
    try {
        const storageItems = await this.kvStore.getAllItems<StorageItem>();
        // 按时间戳排序，最新的在前
        return storageItems
          .map(item => item.data as PickedUpData)
          .sort((a, b) => b.updatedAt - a.updatedAt);
    } catch (error) {
      console.error('Failed to get all picked up orders:', error);
      return [];
    }
  }
  
  /**
   * 删除取件订单数据
   * @param id 订单ID
   * @returns 删除成功返回true，失败返回false
   */
  async deletePickedUpData(id: string): Promise<boolean> {
    try {
        return await this.kvStore.removeItem(id);
    } catch (error) {
      console.error('Failed to delete picked up order:', error);
      return false;
    }
  }
  
  /**
   * 获取取件订单总数
   * @returns 返回订单总数
   */
  async getPickedUpDataCount(): Promise<number> {
    try {
      return await this.kvStore.size();
    } catch (error) {
      console.error('Failed to get picked up order count:', error);
      return 0;
    }
  }
}

const pickedUpDataService = new PickedUpDataService();

export default pickedUpDataService; 